import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	MenuItem,
	Paper,
	Select,
	TextField,
	Typography,
	makeStyles,
} from "@material-ui/core";
import instance from "api/globalaxios";
import SearchCaseForm from "../../Components/SearchCase";
import FormError from "../../Components/FormError";
import OrderDraftsTable from "../../Components/OrderDraftsTable";
import FullscreenToggle from "../../Components/FullscreenToggle";
import {
	allowsMultipleDrafts,
	buildHearingDatesHtml,
	getCaseReferenceNumber,
	nextDraftTitle,
} from "../../utils";
import {
	fetchOrderShortcuts,
	buildShortcutMap,
	createShortcutExpansion,
} from "../../orderShortcuts";

import MicIcon from "@material-ui/icons/Mic";
import { documentPreset } from "Components/RichTextEditor";
import OrderSheetEditor, {
	ORDER_PAGE_ITEMS,
	PostMatterLinks,
	printOrderSheet,
	ShortcutsButton,
	usePostMatterLinks,
} from "../../Components/OrderSheet";

const useStyles = makeStyles(() => ({
	panel: {
		padding: "1.5rem",
		marginTop: "1rem",
		textAlign: "left",
	},
	muted: {
		fontSize: "0.85rem",
		color: TEXT.muted,
	},
}));


// The order sheet toolbar, in TinyMCE's pipe-delimited format. `ordershortcuts`
// and `recordaudio` are supplied as customItems, not built in.
const ORDER_TOOLBAR =
	"undo redo | bold italic underline | alignleft aligncenter alignright alignjustify | bullist numlist | outdent indent | table pagebreak newpage | removeformat | ordershortcuts | recordaudio";

// `prefill` ({ prefix, number, year }) comes from the "Order Drafting" button on
// View Case: the search is filled in and run, which loads that case's drafts.
// The start page as sent to the backend: a whole number from 1 to 100000000.
const startPageNumber = (value) =>
	Math.min(100000000, Math.max(1, parseInt(value, 10) || 1));

export default function OrderDrafting({ prefill }) {
	const classes = useStyles();

	const [searchedCase, setSearchedCase] = useState(null);
	const disputeId = searchedCase?.id || null;
	// Bumped on every successful search, so searching the SAME case again (e.g.
	// clicking Search after arriving pre-searched from View Case) still reloads
	// its drafts - keyed on disputeId alone, a repeat search cleared the list
	// and never re-fetched it.
	const [searchCount, setSearchCount] = useState(0);

	// ---- Drafts list ----
	const [drafts, setDrafts] = useState([]);
	const [draftsLoading, setDraftsLoading] = useState(false);
	const [draftsError, setDraftsError] = useState("");

	// ---- Document formats (ACCB order sheet + the Centre's shared formats) ----
	const [formats, setFormats] = useState([]);
	// The picker doubles as (a) the format for the NEXT new draft and (b) a
	// filter on the drafts list. "all" = show every format; New Draft then
	// defaults to the ACCB order sheet.
	const [newFormat, setNewFormat] = useState("all");
	// The format the editor is currently working in — a new draft uses the
	// picker's value; an existing draft keeps whatever it was saved as. Sent with
	// the save/download body for new drafts so the backend renders the right doc.
	const editorFormatRef = useRef("order_sheet");
	// The format used when creating a new draft (never "all").
	const newDraftFormat = newFormat === "all" ? "order_sheet" : newFormat;
	// One draft per format per case: once that format has a draft, it is edited
	// from the table instead, so New Draft is hidden (also while drafts load).
	// Depositions of PW2 / RW2 are the exception: one draft per witness.
	const canCreateDraft =
		!draftsLoading &&
		(allowsMultipleDrafts(newDraftFormat) ||
			!drafts.some((d) => (d.format || "order_sheet") === newDraftFormat));
	// The list shows only drafts of the selected format ("all" = every format).
	const visibleDrafts = newFormat === "all"
		? drafts
		: drafts.filter((d) => (d.format || "order_sheet") === newFormat);

	// ---- Editor ----
	const editorRef = useRef(null);
	const shortcutListRef = useRef([]);
	const shortcutMapRef = useRef({});
	const [editorOpen, setEditorOpen] = useState(false);
	// Full-screen editor; every way of closing the editor drops back to normal.
	const [editorFullscreen, setEditorFullscreen] = useState(false);
	useEffect(() => {
		if (!editorOpen) setEditorFullscreen(false);
	}, [editorOpen]);
	// Seed HTML for a NEW draft: the pre-filled ACCB order sheet from the
	// backend, so the editor opens on the official format instead of blank.
	const [templateContent, setTemplateContent] = useState("");
	const [templateLoading, setTemplateLoading] = useState(false);
	// null = a brand-new draft; otherwise the draft being edited.
	const [editingDraft, setEditingDraft] = useState(null);
	// The number printed on the first page of the printout / download; later
	// pages continue from it. Saved on the draft (start_page). Held as typed
	// text so the box can be cleared while editing; see startPageNumber.
	const [startPage, setStartPage] = useState("1");
	const startPageRef = useRef("1");
	startPageRef.current = startPage;
	// Identifies the document the editor is mounted for. Not `editingDraft`:
	// auto-save replaces that with the server's copy on every save, which
	// remounted the editor mid-edit and sent the caret to the end.
	const [editorSession, setEditorSession] = useState(null);
	// The draft as it was when the editor opened, for the same reason.
	const openedDraftRef = useRef(null);
	// The order sheet being edited. The editor is controlled, so the document
	// lives here rather than in the editor's DOM.
	const [orderHtml, setOrderHtml] = useState("");
	const orderHtmlRef = useRef("");
	const orderEditorsRef = useRef({});
	// The editor as last focused, so dictation inserts at the caret rather than
	// always at the end of the order.
	const activeOrderEditorRef = useRef(null);

	const [actionLoading, setActionLoading] = useState(false);
	const [saveMessage, setSaveMessage] = useState("");
	const [saveFailed, setSaveFailed] = useState(false);

	// ---- AI Proofread ----
	const [proofreadState, setProofreadState] = useState("idle");
	const [proofreadMessage, setProofreadMessage] = useState("");

	// ---- Dictation ----
	// The recorder's state is mirrored in a ref because the MediaRecorder
	// callbacks and the TinyMCE button read it outside React's render cycle.
	const recorderStateRef = useRef("idle");
	const mediaRecorderRef = useRef(null);
	const chunksRef = useRef([]);
	const [recorderUiState, setRecorderUiState] = useState("idle");
	const [transcribeError, setTranscribeError] = useState("");

	const setRecState = (state) => {
		recorderStateRef.current = state;
		setRecorderUiState(state);
	};

	const draftsUrl = (suffix = "") =>
		`/bac/arbitrator/cases/${disputeId}/order-drafts/${suffix}`;

	// ------------------------------------------------------------------ list
	const fetchDrafts = useCallback(async () => {
		if (!disputeId) return;
		setDraftsLoading(true);
		setDraftsError("");
		try {
			const res = await instance.get(draftsUrl());
			const data = res.data;
			setDrafts(Array.isArray(data) ? data : data.results || []);
		} catch (err) {
			console.error("Failed to load order drafts:", err);
			setDraftsError("Failed to load drafts.");
		} finally {
			setDraftsLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [disputeId]);

	useEffect(() => {
		if (disputeId) fetchDrafts();
		else setDrafts([]);
	}, [disputeId, fetchDrafts, searchCount]);

	// Load the available document formats once (global, not per-case).
	useEffect(() => {
		let alive = true;
		instance
			.get("/bac/arbitrator/order-drafts/formats/")
			.then((res) => {
				if (alive) setFormats(res.data?.formats || []);
			})
			.catch((err) => console.error("Failed to load order formats:", err));
		return () => {
			alive = false;
		};
	}, []);

	// Load the typing shortcuts once for this component.
	useEffect(() => {
		let alive = true;
		fetchOrderShortcuts().then((list) => {
			if (!alive) return;
			shortcutListRef.current = list;
			shortcutMapRef.current = buildShortcutMap(list);
		});
		return () => {
			alive = false;
		};
	}, []);

	const handleSearchSuccess = (caseData) => {
		setSearchedCase(caseData);
		setSearchCount((n) => n + 1);
		// Drafts belong to a case, so nothing from the previous one may linger.
		setDrafts([]);
		setDraftsError("");
		closeEditor();
	};

	// Turn a response blob into a file the browser saves.
	const saveBlob = (blob, filename) => {
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = filename;
		document.body.appendChild(link);
		link.click();
		link.remove();
		window.URL.revokeObjectURL(url);
	};

	const handleDownloadDraft = async (draft) => {
		try {
			const res = await instance.get(
				`${draftsUrl("download/")}?draft_id=${draft.id}`,
				{ responseType: "blob" }
			);
			saveBlob(res.data, `${draft.title || `draft_${draft.id}`}.docx`);
		} catch (err) {
			console.error("Failed to download draft:", err);
			setDraftsError("Could not download that draft. Please try again.");
		}
	};

	// ---------------------------------------------------------------- editor
	const openEditorFor = async (draft, formatKey = "order_sheet") => {
		// A new draft uses the picked format; an existing draft keeps its own.
		editorFormatRef.current = draft ? (draft.format || "order_sheet") : formatKey;
		setEditingDraft(draft);
		setStartPage(String(draft?.start_page || 1));
		openedDraftRef.current = draft || null;
		setEditorSession(draft ? `draft-${draft.id}` : `new-${Date.now()}`);
		setProofreadState("idle");
		setProofreadMessage("");
		setSaveMessage("");
		setSaveFailed(false);
		setTranscribeError("");
		setEditorOpen(true);
		// New draft: pull the official ACCB order-sheet template (arbitrator,
		// A.C. number, parties pre-filled) so the editor is never blank. On
		// failure fall back to a blank sheet rather than blocking drafting.
		if (!draft) {
			setTemplateContent("");
			setTemplateLoading(true);
			try {
				const res = await instance.get(
					draftsUrl(`template/?doc_format=${encodeURIComponent(formatKey)}`)
				);
				setTemplateContent(res.data?.content || "");
			} catch (err) {
				console.error("Failed to load order template:", err);
			} finally {
				setTemplateLoading(false);
			}
		} else {
			setTemplateContent("");
			setTemplateLoading(false);
		}
	};

	const closeEditor = () => {
		// Stop an in-flight recording rather than leaving the mic open.
		if (recorderStateRef.current === "recording" && mediaRecorderRef.current) {
			try {
				mediaRecorderRef.current.stop();
			} catch (err) {
				console.error("Failed to stop the recorder on close:", err);
			}
		}
		setEditorOpen(false);
		setEditingDraft(null);
		setProofreadState("idle");
		setProofreadMessage("");
		setSaveMessage("");
		setSaveFailed(false);
		setTranscribeError("");
		setRecState("idle");
	};

	const editorContent = () =>
		editorRef.current ? editorRef.current.getContent() : "";

	// Print the sheet as it stands in the editor (unsaved edits included)
	// through the browser's own print dialog.
	const handlePrint = () => {
		printOrderSheet(editorContent(), editingDraft?.title || "Order Sheet", {
			caseNumber: caseNumberRef.current,
			startPage: startPageNumber(startPage),
		}).catch((err) => {
			console.error("Failed to open the print window:", err);
			setSaveFailed(true);
			setSaveMessage("Could not open the print window. Please try again.");
		});
	};

	const executeSave = async (title) => {
		setActionLoading(true);
		setSaveMessage("");
		setSaveFailed(false);
		const body = {
			title,
			content: editorContent(),
			start_page: startPageNumber(startPage),
		};
		if (editingDraft) body.draft_id = editingDraft.id;
		else body.format = editorFormatRef.current;
		// The picked date/stage/slot are NOT sent for booking anymore — they are
		// rendered into the order content as a "Post this matter on … for …"
		// hyperlink to the Book Hearing Room tab, which is where booking happens.
		try {
			const res = await instance.post(draftsUrl(), body);
			if (res.data?.success === false) {
				setSaveFailed(true);
				setSaveMessage(res.data?.error || "Failed to save draft.");
				return;
			}
			if (res.data?.draft) setEditingDraft(res.data.draft);
			fetchDrafts();
			setSaveMessage("Draft saved successfully.");
		} catch (err) {
			setSaveFailed(true);
			setSaveMessage(
				err?.response?.data?.error ||
				err?.response?.data?.detail ||
				"Failed to save draft. Please try again."
			);
		} finally {
			setActionLoading(false);
		}
	};

	// Save first instead (that endpoint updates in place when it gets a
	// draft_id), then pull the .docx for the draft that was saved.
	const executeDownload = async (title) => {
		setActionLoading(true);
		setSaveMessage("");
		setSaveFailed(false);
		try {
			const body = {
				title,
				content: editorContent(),
				start_page: startPageNumber(startPage),
			};
			if (editingDraft) body.draft_id = editingDraft.id;
			else body.format = editorFormatRef.current;
			const saved = await instance.post(draftsUrl(), body);

			const draft = saved.data?.draft || editingDraft;
			if (saved.data?.success === false || !draft?.id) {
				setSaveFailed(true);
				setSaveMessage(saved.data?.error || "Could not prepare the download.");
				return;
			}
			setEditingDraft(draft);

			const res = await instance.get(
				`${draftsUrl("download/")}?draft_id=${draft.id}`,
				{ responseType: "blob" }
			);
			saveBlob(res.data, `${draft.title || title}.docx`);
			fetchDrafts();
		} catch (err) {
			console.error("Failed to download the draft:", err);
			setSaveFailed(true);
			setSaveMessage("Download failed. Please try again.");
		} finally {
			setActionLoading(false);
		}
	};

	// Save and Download both run straight away. An existing draft keeps its own
	// title; a new one takes the next name in its sequence (see nextDraftTitle).
	const runDraftAction = (action) => {
		const title =
			editingDraft?.title || nextDraftTitle(drafts, editorFormatRef.current);
		if (action === "save") executeSave(title);
		else executeDownload(title);
	};

	// ------------------------------------------------------------ auto-save
	const editingDraftRef = useRef(editingDraft);
	const actionLoadingRef = useRef(actionLoading);
	const editorOpenRef = useRef(editorOpen);
	const draftsRef = useRef(drafts);

	useEffect(() => {
		editingDraftRef.current = editingDraft;
	}, [editingDraft]);

	useEffect(() => {
		actionLoadingRef.current = actionLoading;
	}, [actionLoading]);

	useEffect(() => {
		editorOpenRef.current = editorOpen;
	}, [editorOpen]);

	useEffect(() => {
		draftsRef.current = drafts;
	}, [drafts]);

	const executeAutoSave = useCallback(async () => {
		if (!disputeId || !editorOpenRef.current || !editorRef.current || actionLoadingRef.current) {
			return;
		}
		if (recorderStateRef.current === "recording" || recorderStateRef.current === "transcribing") {
			return;
		}

		const currentDraft = editingDraftRef.current;
		const title =
			currentDraft?.title ||
			nextDraftTitle(draftsRef.current, editorFormatRef.current);

		const content = editorRef.current.getContent();
		if (!currentDraft && (!content || content.replace(/<[^>]+>/g, "").trim() === "")) {
			return;
		}

		actionLoadingRef.current = true;
		setActionLoading(true);

		const body = {
			title,
			content,
			start_page: startPageNumber(startPageRef.current),
		};
		if (currentDraft?.id) body.draft_id = currentDraft.id;
		else body.format = editorFormatRef.current;

		try {
			const res = await instance.post(draftsUrl(), body);
			if (res.data?.draft) {
				setEditingDraft(res.data.draft);
				editingDraftRef.current = res.data.draft;
			}
			fetchDrafts();
			const timeStr = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
			setSaveFailed(false);
			setSaveMessage(`Draft auto-saved at ${timeStr}.`);
		} catch (err) {
			console.error("Auto-save failed:", err);
		} finally {
			actionLoadingRef.current = false;
			setActionLoading(false);
		}
	}, [disputeId, fetchDrafts]);

	// Auto-save the draft every 1 minute (60,000 ms) while editor is open
	useEffect(() => {
		if (!editorOpen) return;

		const intervalId = setInterval(() => {
			executeAutoSave();
		}, 60000);

		return () => {
			clearInterval(intervalId);
		};
	}, [editorOpen, executeAutoSave]);

	// ------------------------------------------------------------ proofread
	const handleProofread = async () => {
		if (!editorRef.current) return;
		const html = editorRef.current.getContent();
		const plain = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

		if (plain.length < 20) {
			setProofreadState("error");
			setProofreadMessage(
				"Editor content is too short to review (minimum 20 characters)."
			);
			return;
		}

		setProofreadState("loading");
		setProofreadMessage("");
		try {
			const res = await instance.post(
				`/bac/arbitrator/cases/${disputeId}/review-text/`,
				{ text: plain }
			);
			const suggestions = res.data?.suggestions || [];
			let content = editorRef.current.getContent();
			let applied = 0;
			for (const s of suggestions) {
				if (s.original && s.suggestion && content.includes(s.original)) {
					content = content.replace(s.original, s.suggestion);
					applied += 1;
				}
			}
			editorRef.current.setContent(content);
			setProofreadState("done");
			setProofreadMessage(
				`Done — ${applied} correction${applied !== 1 ? "s" : ""} applied.`
			);
		} catch (err) {
			const status = err?.response?.status;
			setProofreadState("error");
			setProofreadMessage(
				status === 403
					? "You do not have access to this case."
					: err?.response?.data?.error ||
					"AI service unavailable, please try again."
			);
		}
	};

	// -------------------------------------------------------------- dictation
	// Held in a ref so the TinyMCE button (registered once, in `setup`) always
	// calls the current closure rather than the one captured at registration.
	const handleMicClick = useRef(null);
	handleMicClick.current = async (editor) => {
		if (recorderStateRef.current === "transcribing") return;

		if (recorderStateRef.current === "recording") {
			mediaRecorderRef.current.stop();
			return;
		}

		setTranscribeError("");
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			chunksRef.current = [];
			const mimeType = MediaRecorder.isTypeSupported("audio/webm")
				? "audio/webm"
				: "";
			const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});

			recorder.ondataavailable = (e) => {
				if (e.data.size > 0) chunksRef.current.push(e.data);
			};

			recorder.onstop = async () => {
				stream.getTracks().forEach((track) => track.stop());
				setRecState("transcribing");

				const blob = new Blob(chunksRef.current, {
					type: recorder.mimeType || "audio/webm",
				});
				const form = new FormData();
				form.append("audio_file", blob, "recording.webm");
				form.append("language", "en");

				try {
					const res = await instance.post(
						`/bac/arbitrator/cases/${disputeId}/transcribe/`,
						form
					);
					editorRef.current.insertContent(res.data?.transcript || "");
					// The case's upcoming stage-wise hearings, appended under the
					// dictated text. Comes back with the transcript, so no extra
					// request.
					const hearingsHtml = buildHearingDatesHtml(
						res.data?.upcoming_hearings
					);
					if (hearingsHtml) editorRef.current.insertContent(hearingsHtml);
				} catch (err) {
					const data = err?.response?.data;
					setTranscribeError(
						err?.response?.status === 400
							? data?.detail || data?.error || "Invalid request. Please try again."
							: "AI service unavailable, please try again."
					);
				} finally {
					setRecState("idle");
				}
			};

			recorder.start();
			mediaRecorderRef.current = recorder;
			setRecState("recording");
		} catch (err) {
			setTranscribeError(
				"Microphone access denied. Please allow microphone permissions."
			);
		}
	};

	// Keep the sheet in a ref as well as state: the save, download, proofread
	// and transcription paths all read it synchronously through the adapter.
	const applyOrderHtml = (html) => {
		orderHtmlRef.current = html || "";
		setOrderHtml(orderHtmlRef.current);
	};

	// Put the caret in the order sheet so typing and dictation land in it.
	const focusOrderRegion = () => {
		const editor = Object.values(orderEditorsRef.current)[0];
		if (editor) editor.commands.focus("end");
		return editor || null;
	};

	// editorRef used to hold the TinyMCE instance. It now holds an adapter with
	// the same methods the rest of this component calls, so the save, download,
	// proofread and transcription paths are unchanged. getContent() returns the
	// whole sheet exactly as before.
	useEffect(() => {
		editorRef.current = {
			getContent: () => orderHtmlRef.current,
			setContent: (html) => applyOrderHtml(html),
			insertContent: (html) => {
				// Insert where the caret is. Dictation used to be preceded by
				// placeCaretInOrderRegion, which collapsed the selection to the end of the
				// order body, so a transcript always landed at the bottom however the
				// drafter had positioned the cursor. Only fall back to the end when no
				// editor has not been focused yet.
				const editors = Object.values(orderEditorsRef.current);
				const target =
				  editors.find((editor) => editor.isFocused) || activeOrderEditorRef.current;
				if (target) {
				  target.chain().focus().insertContent(html).run();
				  return;
				}
				const fallback = focusOrderRegion();
				if (fallback) fallback.chain().focus("end").insertContent(html).run();
			},
			focusOrderRegion,
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Load the sheet when the dialog opens and whenever a different draft is
	// picked. TinyMCE read this once through initialValue; the editor is
	// controlled, so it goes through state.
	useEffect(() => {
		if (!editorOpen) return;
		const opened = openedDraftRef.current;
		applyOrderHtml(opened ? opened.content || "" : templateContent);
		// Deliberately NOT depending on `editingDraft`: auto-save swaps it every
		// minute, and re-pushing that HTML resets the caret. `templateContent`
		// stays, because a new draft's template arrives after the dialog opens.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editorOpen, editorSession, templateContent]);

	// The "Post this matter" links, one per page. A click opens Book Hearing
	// Room in a new tab (a plain click would navigate away and take the unsaved
	// draft with it); on return, the booking made there fills the clicked link
	// and no other. Backend-built, no local storage.
	const {
		handleLinkClick: handleOrderLinkClick,
		getHref: getPostMatterHref,
		getSignature: getPostMatterSignature,
	} = usePostMatterLinks({
		open: editorOpen,
		session: editorSession,
		lineUrl: disputeId ? draftsUrl("post-matter-line/") : null,
		templateUrl:
			disputeId && editorFormatRef.current === "order_sheet"
				? draftsUrl("template/?doc_format=order_sheet")
				: null,
		getHtml: () => orderHtmlRef.current,
		setHtml: applyOrderHtml,
	});

	// Typing shortcuts are an input rule on the editor. The map is read
	// through a getter so the editors can mount before the list has loaded.
	// Case number for the printout's running header (page 2 onward).
	const caseNumberRef = useRef("");
	caseNumberRef.current = searchedCase
		? searchedCase.tiac_custom_dispute_id || ""
		: "";

	const orderPreset = useMemo(
		() => (options) => [
			...documentPreset(options),
			createShortcutExpansion(() => shortcutMapRef.current),
			// Every page the drafter adds gets its own "Post this matter" link.
			PostMatterLinks.configure({
				getHref: getPostMatterHref,
				getSignature: getPostMatterSignature,
				isOrderSheet: () => editorFormatRef.current === "order_sheet",
			}),
		],
		[getPostMatterHref, getPostMatterSignature]
	);

	// recordaudio and ordershortcuts as React toolbar items. The TinyMCE
	// versions needed micBtnApiRef to push state into the button imperatively;
	// here the button just reads recorderUiState.
	const orderToolbarItems = useMemo(
		() => ({
			// Page break / New page, kept in the order table.
			...ORDER_PAGE_ITEMS,
			recordaudio: {
				title:
					"Record audio - click to start, click again to stop & transcribe",
				icon: <MicIcon fontSize="small" />,
				active: () => recorderUiState === "recording",
				disabled: () => recorderUiState === "transcribing",
				run: (editor) => {
					if (handleMicClick.current) handleMicClick.current(editor);
				},
			},
			ordershortcuts: {
				title: "Typing shortcuts",
				render: () => (
					<ShortcutsButton getList={() => shortcutListRef.current} />
				),
			},
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[recorderUiState]
	);

	const refNo = getCaseReferenceNumber(searchedCase);

	// ----------------------------------------------------------------- render
	return (
		<Box>

			<SearchCaseForm
				handleSearchSuccess={handleSearchSuccess}
				initialValues={prefill}
			/>

			{searchedCase && (
				<Paper className={classes.panel}>
					<Box
						display="flex"
						justifyContent="space-between"
						alignItems="center"
						flexWrap="wrap"
						style={{ marginBottom: "1rem", gap: "0.5rem" }}
					>
						<Box>
							<Typography variant="h6" color="primary">
								{searchedCase.tiac_custom_dispute_id}
							</Typography>
							{refNo && (
								<Typography className={classes.muted} style={{ overflowWrap: "anywhere" }}>
									{refNo.label}: {refNo.value}
								</Typography>
							)}
							{searchedCase.name && (
								<Typography className={classes.muted}>
									{searchedCase.name}
								</Typography>
							)}
						</Box>
						{/* Grows to the free width and may shrink the picker: on a phone a
						    fixed-width picker squeezed the button onto three lines and out
						    of the card, and "stretch" pulled the picker to that height. */}
						<Box
							display="flex"
							alignItems="stretch"
							justifyContent="flex-end"
							style={{ gap: "0.5rem", flex: "1 1 auto", maxWidth: "100%" }}
						>
							<Select
								variant="outlined"
								margin="dense"
								value={newFormat}
								onChange={(e) => setNewFormat(e.target.value)}
								style={{ flex: "0 1 260px", minWidth: 0, background: "#fff" }}
							>
								<MenuItem value="all">All Formats</MenuItem>
								{(formats.length
									? formats
									: [{ key: "order_sheet", label: "ACCB Order Sheet" }]
								).map((f) => (
									<MenuItem key={f.key} value={f.key}>
										{f.label}
									</MenuItem>
								))}
							</Select>
							{canCreateDraft && (
								<Button
									variant="contained"
									color="primary"
									onClick={() => openEditorFor(null, newDraftFormat)}
									style={{ flexShrink: 0, whiteSpace: "nowrap" }}
								>
									{allowsMultipleDrafts(newDraftFormat)
										? `+ New ${nextDraftTitle(drafts, newDraftFormat)}`
										: "+ New Draft"}
								</Button>
							)}
						</Box>
					</Box>

					{draftsLoading ? (
						<Box display="flex" alignItems="center" style={{ gap: "0.5rem" }}>
							<CircularProgress size={16} />
							<Typography className={classes.muted}>Loading drafts…</Typography>
						</Box>
					) : draftsError ? (
						<FormError message={draftsError} />
					) : visibleDrafts.length === 0 ? (
						<Typography className={classes.muted}>
							{newFormat === "all"
								? 'No drafts yet. Click "+ New Draft" to create one.'
								: 'No drafts in this format yet. Click "+ New Draft" to create one.'}
						</Typography>
					) : (
						<OrderDraftsTable
							drafts={visibleDrafts}
							formats={formats}
							onDownload={handleDownloadDraft}
							onEdit={(draft) => openEditorFor(draft)}
						/>
					)}
				</Paper>
			)}

			{/* ---- Editor ---- */}
			<Dialog
				open={editorOpen}
				onClose={closeEditor}
				fullWidth
				maxWidth="md"
				fullScreen={editorFullscreen}
				disableBackdropClick
				// A definite height - MUI's own max - so the sheet below can fill
				// it (fillHeight) instead of overflowing it. Fullscreen is 100%.
				PaperProps={editorFullscreen ? undefined : { style: { height: "calc(100% - 64px)" } }}
			>
				<DialogTitle disableTypography>
					<Box
						display="flex"
						justifyContent="space-between"
						alignItems="center"
						flexWrap="wrap"
						style={{ gap: "0.5rem" }}
					>
						<Typography variant="h6">
							{editingDraft ? `Editing: ${editingDraft.title}` : "New Draft"}
						</Typography>
						<Box display="flex" alignItems="center" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
							<Button
								variant="outlined"
								color="primary"
								size="small"
								disabled={proofreadState === "loading"}
								onClick={handleProofread}
								style={{ textTransform: "none", minWidth: "110px" }}
							>
								{proofreadState === "loading" ? (
									<Box display="flex" alignItems="center" style={{ gap: "0.4rem" }}>
										<CircularProgress size={13} color="inherit" />
										<span>Reviewing...</span>
									</Box>
								) : (
									"AI Proofread"
								)}
							</Button>
							<Button
								variant="outlined"
								color="primary"
								size="small"
								disabled={actionLoading}
								onClick={() => runDraftAction("save")}
								style={{ textTransform: "none" }}
							>
								Save Draft
							</Button>
							<Button
								variant="outlined"
								color="primary"
								size="small"
								disabled={actionLoading}
								onClick={() => runDraftAction("download")}
								style={{ textTransform: "none" }}
							>
								Download
							</Button>
							{/* Inline label rather than TextField's floating one: an outlined
							    field is 40px tall even at size="small", next to ~26px buttons,
							    and squeezed to their height the floating label crowds the value. */}
							<Box
								component="label"
								display="flex"
								alignItems="center"
								title="Number on the first page of the printout and download; later pages continue from it"
								style={{ gap: "0.35rem" }}
							>
								<Typography
									component="span"
									style={{ fontSize: "0.8125rem", color: "rgba(0, 0, 0, 0.6)", whiteSpace: "nowrap" }}
								>
									Start page
								</Typography>
								<TextField
									type="number"
									size="small"
									variant="outlined"
									value={startPage}
									onChange={(e) => setStartPage(e.target.value)}
									onBlur={() => setStartPage(String(startPageNumber(startPage)))}
									// Same font and height as the size="small" buttons beside it.
									InputProps={{ style: { fontSize: "0.8125rem", lineHeight: 1.4 } }}
									inputProps={{
										min: 1,
										max: 100000000,
										"aria-label": "Start page number",
										style: { padding: "4px 8px", height: "auto" },
									}}
									style={{ width: 90 }}
								/>
							</Box>
							<Button
								variant="outlined"
								color="primary"
								size="small"
								onClick={handlePrint}
								style={{ textTransform: "none" }}
							>
								Print
							</Button>
							<FullscreenToggle
								fullscreen={editorFullscreen}
								onToggle={() => setEditorFullscreen((v) => !v)}
							/>
							<Button onClick={closeEditor} style={{ textTransform: "none" }}>
								Close
							</Button>
						</Box>
					</Box>
				</DialogTitle>

				<DialogContent dividers style={{ display: "flex", flexDirection: "column" }}>
					{templateLoading && !editingDraft ? (
						<Box display="flex" alignItems="center" style={{ gap: "0.5rem", minHeight: 180 }}>
							<CircularProgress size={18} />
							<Typography style={{ color: TEXT.secondary, fontSize: "13px" }}>
								Loading order template...
							</Typography>
						</Box>
					) : (
						<>
							{/* The order sheet. Editable throughout, as under TinyMCE. */}
							<OrderSheetEditor
								// Keyed on the open session so a different draft starts clean.
								key={editorSession}
								value={orderHtml}
								onChange={applyOrderHtml}
								preset={orderPreset}
								toolbar={ORDER_TOOLBAR}
								fillHeight
								// Ctrl+S / Ctrl+P do what the Save Draft and Print buttons do,
								// including Save Draft's guard against a save already in flight.
								onSave={() => {
									if (!actionLoading) runDraftAction("save");
								}}
								onPrint={handlePrint}
								customItems={orderToolbarItems}
								onLinkClick={handleOrderLinkClick}
								onActiveEditorChange={(editor) => {
									activeOrderEditorRef.current = editor;
								}}
								onReady={(editors) => {
									orderEditorsRef.current = editors;
									focusOrderRegion();
								}}
							/>
						</>
					)}

					{/* Recording, transcribing, proofread and save feedback. Sticky:
						these share the dialog body's scroll with the sheet, so without
						it they scroll out of sight exactly when they matter. */}
					<Box className="order-sheet__status">
						{recorderUiState === "recording" && (
							<Box display="flex" alignItems="center" style={{ gap: "0.5rem", marginTop: "0.5rem" }}>
								<span
									style={{
										display: "inline-block",
										width: "10px",
										height: "10px",
										borderRadius: "50%",
										background: "#d32f2f",
										animation: "accb-mic-pulse 1s infinite",
									}}
								/>
								<Typography style={{ color: TEXT.error, fontSize: "13px" }}>
									Recording… Click the microphone button again to stop.
								</Typography>
								<style>
									{`@keyframes accb-mic-pulse { 0%,100%{opacity:1} 50%{opacity:0.2} }`}
								</style>
							</Box>
						)}
						{recorderUiState === "transcribing" && (
							<Box display="flex" alignItems="center" style={{ gap: "0.5rem", marginTop: "0.5rem" }}>
								<CircularProgress size={14} />
								<Typography style={{ color: TEXT.secondary, fontSize: "13px" }}>
									Transcribing audio…
								</Typography>
							</Box>
						)}

						{transcribeError && <FormError message={transcribeError} />}
						{proofreadMessage && (
							<Typography
								style={{
									marginTop: "0.5rem",
									fontSize: "13px",
									color: proofreadState === "error" ? "#d32f2f" : "#2e7d32",
								}}
							>
								{proofreadMessage}
							</Typography>
						)}
						{saveMessage && (
							<Typography
								style={{
									marginTop: "0.5rem",
									fontSize: "13px",
									color: saveFailed ? "#d32f2f" : "#2e7d32",
								}}
							>
								{saveMessage}
							</Typography>
						)}
					</Box>

				</DialogContent>
			</Dialog>
		</Box>
	);
}